// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useState, useEffect, useRef } from "react";
import Modal from "@cloudscape-design/components/modal";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Input, { type InputProps } from "@cloudscape-design/components/input";
import SpaceBetween from "@cloudscape-design/components/space-between";

export interface PinDialogProps {
  visible: boolean;
  onSubmit: (pin: string) => void;
  onCancel: () => void;
  errorMessage?: string;
}

export default function PinDialog({
  visible,
  onSubmit,
  onCancel,
  errorMessage,
}: PinDialogProps): React.ReactElement {
  const [pin, setPin] = useState("");
  const inputRef = useRef<InputProps.Ref>(null);

  // Clear input and focus when dialog opens
  useEffect(() => {
    if (visible) {
      setPin("");
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [visible]);

  const handleSubmit = () => {
    if (pin.length > 0) {
      onSubmit(pin);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onCancel}
      header="Enter YubiKey PIN"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={pin.length === 0}>
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField label="PIN" errorText={errorMessage}>
        <Input
          ref={inputRef}
          type="password"
          value={pin}
          onChange={({ detail }) => setPin(detail.value)}
          onKeyDown={({ detail }) => {
            if (detail.key === "Enter" && pin.length > 0) {
              handleSubmit();
            }
          }}
          autoComplete={false}
        />
      </FormField>
    </Modal>
  );
}
